import { postJSON } from "../api.js";
import { optionsCache } from "../app/shell.js";
import { esc, opt } from "../ui/format.js";
import { uiMoney } from "../ui/kit.js";
import { toast } from "../ui/modals.js";

/**
 * The meeting room — a live conversation after a business meeting or a
 * country visit is requested.
 *
 * The other side asks three questions (typed out). You pick an answer; they
 * react, and the deal on the table visibly moves: the odds meter, the deal's
 * value (meetings), or the investment asked and the incentive offered
 * (visits). Ends with what you walked out with versus what you walked in
 * with. Data and actions: /api/conversation/start|answer (backend
 * conversations.py, routed through engine.execute_action).
 * Reuses the CEO Agenda stage look (.ag-stage).
 */

function cvTerms(kind, d) {
  const pct = (p) => `${Math.round((p || 0) * 100)}%`;
  if (kind === "one_on_one") {
    return `
      <div class="cv-term"><span>Loyalty</span><b>${Math.round(d.loyalty)}</b><div class="cv-bar"><i style="width:${d.loyalty}%"></i></div></div>
      <div class="cv-term"><span>Ambition</span><b>${Math.round(d.ambition)}</b></div>
      <div class="cv-term"><span>Flight risk</span><b>${d.flight_risk ?? "—"}%</b></div>
      <div class="cv-term"><span>Salary</span><b>${uiMoney(d.salary, { compact: true })}</b></div>`;
  }
  if (kind === "meeting") {
    return `
      <div class="cv-term"><span>Chance it closes</span><b>${pct(d.probability)}</b><div class="cv-bar"><i style="width:${pct(d.probability)}"></i></div></div>
      <div class="cv-term"><span>Deal value a year</span><b>${uiMoney(d.value_low, { compact: true })}–${uiMoney(d.value_high, { compact: true })}</b></div>`;
  }
  const inc = d.incentive_type === "Tax Holiday" ? `${Math.round(d.incentive_value * 100)}% off tax`
    : d.incentive_type === "Cash Subsidy Grant" ? uiMoney(d.incentive_value, { compact: true })
    : `+${(d.incentive_value * 100).toFixed(1)}%/mo growth`;
  return `
    <div class="cv-term"><span>Chance they sign</span><b>${pct(d.probability)}</b><div class="cv-bar"><i style="width:${pct(d.probability)}"></i></div></div>
    <div class="cv-term"><span>They offer</span><b>${esc(inc)}</b></div>
    <div class="cv-term"><span>They want you to invest</span><b>${uiMoney(d.investment_required, { compact: true })}</b></div>`;
}

function cvChanges(kind, ch, trust, capped) {
  const out = [];
  const pts = (v) => `${v > 0 ? "+" : ""}${Math.round(v * 100)}pt`;
  if (ch.probability) out.push([ch.probability > 0, `Odds ${pts(ch.probability)}`]);
  if (ch.value_high) out.push([ch.value_high > 0, `Deal value ${ch.value_high > 0 ? "up" : "down"} ${uiMoney(Math.abs(ch.value_high), { compact: true })}`]);
  if (ch.investment_required) out.push([ch.investment_required < 0, `Investment asked ${ch.investment_required > 0 ? "+" : "−"}${uiMoney(Math.abs(ch.investment_required), { compact: true })}`]);
  if (ch.incentive_value) out.push([ch.incentive_value > 0, `Incentive ${ch.incentive_value > 0 ? "improved" : "reduced"}`]);
  if (trust) out.push([trust > 0, `Trust ${trust > 0 ? "+" : ""}${trust}`]);
  if (ch.loyalty) out.push([ch.loyalty > 0, `Loyalty ${ch.loyalty > 0 ? "+" : ""}${Math.round(ch.loyalty)}`]);
  if (ch.ambition) out.push([ch.ambition < 0, `Ambition ${ch.ambition > 0 ? "+" : ""}${Math.round(ch.ambition)}`]);
  if (ch.flight_risk) out.push([ch.flight_risk < 0, `Flight risk ${ch.flight_risk > 0 ? "+" : ""}${ch.flight_risk.toFixed(1)}pt`]);
  if (ch.salary) out.push([false, `Salary +${uiMoney(ch.salary, { compact: true })}/yr`]);
  if (!out.length) return `<span class="ag-eff zero">${capped ? "Odds already at the maximum" : "No change"}</span>`;
  return out.map(([good, t]) => `<span class="ag-eff ${good ? "up" : "down"}">${good ? "▲" : "▼"} ${esc(t)}</span>`).join("");
}

async function openConversation({ kind, name, refId = null, onClose = null }) {
  let s;
  try {
    s = await postJSON("/conversation/start", { kind, name, ref_id: refId });
  } catch (e) { toast(e.message); onClose && onClose(); return; }
  const startDeal = { ...s.deal };
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop ag-stage-wrap";
  backdrop.innerHTML = `
    <div class="ag-stage cv-stage ${kind === "visit" ? "ag-government" : kind === "one_on_one" ? "ag-townhall" : "ag-partner"}">
      <div class="ag-lights"><i></i><i></i><i></i></div>
      <header class="ag-stage-head">
        <div><div class="sx-eyebrow">${kind === "visit" ? "🏛" : kind === "one_on_one" ? "☕" : "🤝"} ${esc(s.header.eyebrow)}</div><h3>${esc(s.header.title)}</h3></div>
        <div class="ag-dots" id="cv-dots"></div>
      </header>
      <div class="cv-terms" id="cv-terms">${cvTerms(kind, s.deal)}</div>
      <div class="ag-floor" id="cv-floor"></div>
      <footer class="ag-stage-foot"><span>${kind === "visit" ? "Their formal offer arrives next month." : kind === "one_on_one" ? "Your answers change their loyalty and the team's mood." : "Their written terms arrive next month."}</span>
        <div class="modal-actions"><button id="cv-leave">Leave the room</button><button class="primary hidden" id="cv-next">Next question</button></div></footer>
    </div>`;
  document.body.appendChild(backdrop);
  const $c = (q) => backdrop.querySelector(q);
  const close = () => { backdrop.remove(); window.refreshAll && window.refreshAll(); onClose && onClose(); };
  $c("#cv-leave").addEventListener("click", close);
  const dots = (n, total) => { $c("#cv-dots").innerHTML = Array.from({ length: total }, (_, i) => `<i class="${i < n ? "done" : i === n ? "on" : ""}"></i>`).join(""); };

  const ask = (q, answered, total) => {
    dots(answered, total);
    $c("#cv-next").classList.add("hidden");
    $c("#cv-floor").innerHTML = `
      <div class="ag-asker${q.press ? " press" : ""}"><div class="ag-mic">${q.press ? "🎤" : kind === "visit" ? "🏛" : kind === "one_on_one" ? "☕" : "💼"}</div><div class="ag-bubble">
        <div class="ag-qnum">${esc(q.asker || s.header.asker)} · question ${q.index + 1} of ${total}</div><p id="cv-q"></p></div></div>
      <div class="ag-options">${q.options.map((o) => `
        <button type="button" class="ag-option" data-opt="${o.index}" disabled>
          <span class="ag-style">${esc(o.style)}${o.risky ? ` <em title="Could land well or backfire">⚡ risky</em>` : ""}</span>
          <span class="ag-label">${esc(o.label)}</span></button>`).join("")}</div>
      <div class="ag-reaction hidden" id="cv-reaction"></div>`;
    window.agType($c("#cv-q"), q.text, () => {
      backdrop.querySelectorAll(".ag-option").forEach((b, i) => {
        setTimeout(() => { b.disabled = false; b.classList.add("in"); }, i * 90);
        b.addEventListener("click", () => choose(Number(b.dataset.opt), b));
      });
    });
  };

  const choose = async (opt, btn) => {
    backdrop.querySelectorAll(".ag-option").forEach((b) => { b.disabled = true; if (b !== btn) b.classList.add("faded"); });
    btn.classList.add("picked");
    let r;
    try {
      r = await postJSON("/conversation/answer", { kind, ref_id: s.ref_id, option: opt });
    } catch (e) { toast(e.message); return; }
    const good = (r.changes.probability || 0) > 0 || (r.trust || 0) > 0 || (r.changes.loyalty || 0) > 0;
    const mood = r.backfired ? "boo" : good ? "cheer" : "murmur";
    backdrop.querySelector(".ag-stage").dataset.mood = mood;
    $c("#cv-terms").innerHTML = cvTerms(kind, r.deal);
    $c("#cv-terms").classList.remove("flash"); void $c("#cv-terms").offsetWidth; $c("#cv-terms").classList.add("flash");
    const rx = $c("#cv-reaction");
    rx.className = `ag-reaction ${mood}`;
    rx.innerHTML = `<div class="ag-rx-title">${r.backfired ? "⚡ That backfired" : good ? "It landed" : "They take note"}</div>
      <p>${esc(r.reaction)}</p><div class="ag-effs">${cvChanges(kind, r.changes, r.trust, r.odds_capped)}</div>`;
    dots(r.answered, r.total);
    const next = $c("#cv-next");
    next.classList.remove("hidden");
    if (r.done) {
      next.textContent = "Walk out";
      next.onclick = () => finish(r);
    } else {
      next.textContent = "Next question";
      next.onclick = () => { delete backdrop.querySelector(".ag-stage").dataset.mood; ask(r.next_question, r.answered, r.total); };
    }
  };

  const finish = (r) => {
    $c("#cv-next").classList.add("hidden");
    $c("#cv-leave").textContent = "Done";
    $c("#cv-floor").innerHTML = `
      <div class="ag-paper">
        <div class="ag-paper-mast">Meeting notes · ${esc(s.header.host)}</div>
        <h2>${r.answers.some((a) => a.backfired) ? "A bumpy meeting" : (r.deal.probability > startDeal.probability ? "You moved them" : "A cautious meeting")}</h2>
        <div class="cv-compare"><div><span>Walked in</span>${cvTerms(kind, startDeal)}</div><div><span>Walked out</span>${cvTerms(kind, r.deal)}</div></div>
        <ol class="ag-recap">${r.answers.map((a) => `<li><div class="ag-recap-q">${esc(a.question)}</div>
          <div class="ag-recap-a"><b>${esc(a.style)}:</b> ${esc(a.answer)}${a.backfired ? ` <span class="neg">(backfired)</span>` : ""}</div>
          <div class="ag-recap-r">${esc(a.reaction)}</div></li>`).join("")}</ol>
      </div>`;
  };

  if (s.question) ask(s.question, s.answered, s.total);
  else finish({ answers: s.answers, deal: s.deal });
}

// Country visits open the conversation straight after the request.
function openVisitCountryModal() {
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal">
      <h3>Visit a country</h3>
      <p style="font-size:12.5px; color:var(--text-mid); margin:-6px 0 12px;">You'll meet a senior official in person. They'll ask you questions, and your answers shape the offer they make next month.</p>
      <div class="field"><label>Country</label><select id="modal-country">${opt(optionsCache.countries)}</select></div>
      <div class="modal-actions"><button id="modal-cancel">Cancel</button><button class="primary" id="modal-ok">Travel and meet</button></div>
    </div>`;
  document.body.appendChild(backdrop);
  backdrop.querySelector("#modal-cancel").addEventListener("click", () => backdrop.remove());
  backdrop.querySelector("#modal-ok").addEventListener("click", async () => {
    const country = backdrop.querySelector("#modal-country").value;
    backdrop.remove();
    try {
      const res = await postJSON("/government/visit", { company_name: country });
      toast(res.message);
    } catch (e) { toast(e.message); return; }
    openConversation({ kind: "visit", name: country });
  });
}

// Late-bound: a later view may replace these (window.X = ...), so callers use window.X.
window.openConversation = openConversation;
window.openVisitCountryModal = openVisitCountryModal;

export { openConversation };
